import { createSlice, createSelector } from '@reduxjs/toolkit';
import { nucleusApi } from '@/app/services/nucleus';

const initialState = {
  roles: [],
  companyType: {},
  divisions: {},
  invoiceDeliveryMethods: {},
  operators: {},
  states: [],
  stateMap: {},
  nlcCompactStatus: {},
  ptCompactStatus: {},
  otCompactStatus: {},
  jobSources: [],
  priority: {},
  operatorMapping: {
    eq: '=',
    gt: '>',
    gte: '>=',
    lt: '<',
    lte: '<=',
    ne: '!=',
    ilcontains: 'has',
    ircontains: 'in',
  },
  eventSource: {},
  entityType: {},
  subscriptionType: {},
};

export const enumSlice = createSlice({
  name: 'enum',
  initialState,
  extraReducers: (builder) => {
    builder.addMatcher(nucleusApi.endpoints.getEnums.matchFulfilled, (state, { payload }) => {
      if (payload?.divisions) {
        state.divisions = payload?.divisions;
      }
      if (payload?.auth_user_role) {
        state.roles = payload?.auth_user_role;
      }
      if (payload?.job_sources) {
        state.jobSources = payload?.job_sources;
      }
      if (payload?.company_type) {
        state.companyType = payload?.company_type;
      }
      if (payload?.comparison_operator) {
        state.operators = payload?.comparison_operator;
      }
      if (payload?.state) {
        state.states = Object.values(payload?.state);
      }
      if (payload?.state) {
        state.stateMap = payload.state;
      }
      if (payload?.invoice_delivery_methods) {
        state.invoiceDeliveryMethods = payload?.invoice_delivery_methods;
      }
      if (payload?.entity_type) {
        state.entityType = payload?.entity_type;
      }
      if (payload?.event_source) {
        state.eventSource = payload?.event_source;
      }
      if (payload?.job_priority_type) {
        state.priority = payload?.job_priority_type;
      }
      if (payload?.subscription_type) {
        state.subscriptionType = payload?.subscription_type;
      }
      if (payload?.n_l_c_compact_status) {
        state.nlcCompactStatus = payload?.n_l_c_compact_status;
      }
      if (payload?.p_t_compact_status) {
        state.ptCompactStatus = payload?.p_t_compact_status;
      }
      if (payload?.o_t_compact_status) {
        state.otCompactStatus = payload?.o_t_compact_status;
      }
    });
  },
});

export const { setActivityType } = enumSlice.actions;

export const selectAllEnum = (state) => state.enum;
export const selectCompanyType = (state) => state.enum.companyType;
export const selectCompanyTypeList = createSelector(selectCompanyType, (companyType) => {
  return Object.values(companyType).map((ct) => {
    let display = ct;
    if (display.includes('_')) display = display.replace('_', ' ');
    const words = display.split(' ');

    for (let i = 0; i < words.length; i++) {
      words[i] = words[i][0].toUpperCase() + words[i].substr(1);
    }
    const label = words.join(' ');
    return {
      value: ct,
      label: label.toLowerCase() === 'msp' ? label.toUpperCase() : label,
    };
  });
});
export const selectJobSources = (state) => state.enum.jobSources;
export const selectJobSourcesList = createSelector(selectJobSources, (source) => {
  return Object.values(source).map((div) => ({ value: div, label: div }));
});
export const selectPriority = (state) => state.enum.priority;
export const selectPriorityList = createSelector(selectPriority, (source) => {
  return Object.entries(source).map(([key, value]) => ({ value: key, label: value }));
});

export const selectSubscriptionType = (state) => state.enum?.subscriptionType || {};
export const selectSubscriptionTypeList = createSelector(selectSubscriptionType, (subscriptions) => {
  return Object.values(subscriptions).map((sub) => ({ value: sub, label: sub }));
});

export const selectEntityType = (state) => state.enum.entityType;
export const selectEntityTypeList = createSelector(selectEntityType, (source) => {
  return Object.values(source).map((div) => ({ value: div, label: div }));
});
export const selectStates = (state) => state.enum.states;
export const selectStateList = createSelector(selectStates, (states) => {
  return states.map((state, i) => ({ value: i, label: state }));
});
export const selectStateMap = (state) => state.enum.stateMap;

export const selectRoles = (state) => state.enum.roles;
export const selectRolesList = createSelector(selectRoles, (roles) => {
  if (typeof roles !== 'object' || roles === null) {
    return [];
  }
  return Object.values(roles).map((role) => ({ value: role, label: role }));
});
export const selectOperators = (state) => state.enum.operators;
export const selectOperatorMapping = (state) => state.enum.operatorMapping;
export const selectOperatorList = createSelector(selectOperators, selectOperatorMapping, (op, mapping) => {
  return Object.keys(op).reduce((result, key) => {
    if (mapping && Object.keys(mapping).includes(key)) {
      result.push({ label: mapping[key], value: op[key] });
    }
    return result;
  }, []);
});

export const selectEventSource = (state) => state.enum.eventSource;
export const selectEventSourceList = createSelector(selectEventSource, (source) => {
  let values = Object.values(source).map((div) => ({ value: div, label: div }));
  values.push({ value: 'bullhorn_placement', label: 'bullhorn_placement' });
  values.push({ value: 'bullhorn_job_submission', label: 'bullhorn_job_submission' });
  return values;
});
export const selectSystemIdList = createSelector(selectEventSource, (source) => {
  return Object.values(source).map((div) => ({ value: '', label: div, is_active: true, valid: true }));
});
export const selectDivisions = (state) => state.enum.divisions;
export const selectDivisionsList = createSelector(selectDivisions, (divisions) => {
  return Object.values(divisions).map((div) => ({ value: div, label: div }));
});
export const selectInvoiceDeliveryMethods = (state) => state.enum.invoiceDeliveryMethods;
export const selectNLCCompactStatus = (state) => state.enum.nlcCompactStatus;
export const selectNLCCompactStatusList = createSelector(selectNLCCompactStatus, (status) => {
  return Object.values(status).map((value) => ({ value: value, label: value }));
});
export const selectPTCompactStatus = (state) => state.enum.ptCompactStatus;
export const selectPTCompactStatusList = createSelector(selectPTCompactStatus, (status) => {
  return Object.values(status).map((value) => ({ value: value, label: value }));
});
export const selectOTCompactStatus = (state) => state.enum.otCompactStatus;
export const selectOTCompactStatusList = createSelector(selectOTCompactStatus, (status) => {
  return Object.values(status).map((value) => ({ value: value, label: value }));
});

export default enumSlice.reducer;
